import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { blockCatalog, defaultInquiryForm, hasPlaceholder, landingContentSchema, lifecycleOf, type Block, type BlockGroup, type BlockType, type LandingContent, type LandingDraft, type PublishResult } from '@agency/contracts';
import { renderBlocks } from '@agency/public-blocks';
import { Badge, Button, Drawer, EmptyState, Icon, IconButton, Notice, Segmented, SelectField, Skeleton, TextAreaField, TextField, useToast } from '@agency/ui';
import { ApiClientError, del, get, post, put } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtDate } from '../../lib/format.ts';
import { ConflictNotice, QueryError } from '../../lib/query-state.tsx';
import { BlockInspector } from './block-inspector.tsx';
import { ChecksDrawer, PageSettings, ScheduleDrawer, VersionsDrawer } from './editor-panels.tsx';
import './landings.css';

type SaveState = 'saved' | 'dirty' | 'saving' | 'offline' | 'conflict' | 'invalid' | 'error';
type Device = 'desktop' | 'tablet' | 'mobile';

const BLOCK_LABEL = Object.fromEntries(Object.values(blockCatalog).map((c) => [c.type, c.label])) as Record<BlockType, string>;
const GROUP_ORDER: BlockGroup[] = ['Opening', 'Explain', 'Proof', 'Convert', 'Connect'];
const uid = () => Math.random().toString(36).slice(2, 8);
const newBlock = (type: BlockType): Block => blockCatalog[type].create(`${type}-${uid()}`) as Block;

function issuesFor(content: LandingContent | null) {
  if (!content) return [];
  const r = landingContentSchema.safeParse(content);
  return r.success ? [] : r.error.issues.map((i) => ({ path: i.path.map(String), message: i.message }));
}

/** Inert inquiry form for the canvas: shows the real field layout without submitting anything. */
function CanvasForm() {
  return (
    <form className="pb-form" onSubmit={(e) => e.preventDefault()} aria-label="Inquiry form preview (not interactive)">
      {defaultInquiryForm.fields.map((f) => (
        <div className="pb-form__field" key={f.name}>
          <span className="pb-form__label">{f.label}{f.required ? null : <span className="pb-form__opt"> (optional)</span>}</span>
          <div className={'pb-form__input'} style={{ minHeight: f.type === 'textarea' ? 120 : undefined }} aria-hidden="true" />
        </div>
      ))}
      <div><span className="pb-btn pb-btn--primary">{defaultInquiryForm.submitLabel}</span></div>
    </form>
  );
}

export function EditorPage() {
  const { variantId = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const canEdit = can('landings.edit');
  const canPublish = can('landings.publish');

  const draftQ = useQuery<LandingDraft, ApiClientError>({ queryKey: ['draft', variantId], queryFn: () => get(`/admin/landings/${variantId}/draft`), refetchOnWindowFocus: false, staleTime: Infinity });
  const [content, setContent] = useState<LandingContent | null>(null);
  const [revision, setRevision] = useState(0);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('seo');
  const [device, setDevice] = useState<Device>('desktop');
  const [publishOpen, setPublishOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [checksOpen, setChecksOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [summary, setSummary] = useState('');
  const [savedAt, setSavedAt] = useState(0);
  const [wfBusy, setWfBusy] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<ApiClientError | null>(null);
  const [undo, setUndo] = useState<LandingContent | null>(null);
  const [recovered, setRecovered] = useState(false);
  const savedJson = useRef('');
  const canvasRef = useRef<HTMLDivElement>(null);
  const inspectorRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [announce, setAnnounce] = useState('');
  const prevState = useRef<SaveState>('saved');
  const [addType, setAddType] = useState<'' | BlockType>('');
  const [zoom, setZoom] = useState(1);
  const storeKey = `agency-draft:${variantId}`;

  // Load the server draft; recover unsaved local work if the same base revision is still current (network/session loss, D123).
  useEffect(() => {
    const d = draftQ.data;
    if (!d) return;
    setRevision(d.revision);
    savedJson.current = JSON.stringify(d.content);
    let next = d.content;
    try {
      const raw = sessionStorage.getItem(storeKey);
      if (raw) {
        const stored = JSON.parse(raw) as { base: number; content: LandingContent };
        if (stored.base === d.revision && JSON.stringify(stored.content) !== savedJson.current) { next = stored.content; setRecovered(true); setSaveState('dirty'); }
        else sessionStorage.removeItem(storeKey);
      }
    } catch { /* ignore corrupt local copy */ }
    setContent(next);
    setSelected(next.blocks[0]?.id ?? 'seo');
  }, [draftQ.data, storeKey]);

  // Render the canvas at the true device width (so container queries behave as on a real device) and scale to fit.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const widths: Record<Device, number> = { desktop: 1280, tablet: 768, mobile: 390 };
    const fit = () => setZoom(Math.min(1, (el.clientWidth - 48) / widths[device]));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [device, content !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // One quiet live region for save outcomes: not for every keystroke ("Saving…" / "Unsaved changes…" stay silent).
  useEffect(() => {
    const prev = prevState.current;
    prevState.current = saveState;
    if (saveState === 'saved' && prev === 'saving') setAnnounce('Draft saved.');
    else if (saveState === 'offline') setAnnounce('You are offline. Your changes are kept and will be saved when the connection returns.');
    else if (saveState === 'conflict') setAnnounce('This draft was changed elsewhere. Reload the latest version before saving.');
    else if (saveState === 'error') setAnnounce('Saving failed. Your changes are kept in this tab.');
    else if (saveState === 'invalid' && prev !== 'invalid') setAnnounce('Not saved: some fields need attention.');
  }, [saveState]);

  const issues = useMemo(() => issuesFor(content), [content]);
  const dirty = content !== null && JSON.stringify(content) !== savedJson.current;

  // Keep a local copy of unsaved work.
  useEffect(() => {
    if (!content) return;
    if (dirty) sessionStorage.setItem(storeKey, JSON.stringify({ base: revision, content }));
    else sessionStorage.removeItem(storeKey);
  }, [content, dirty, revision, storeKey]);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const save = useCallback(async () => {
    if (!content || !canEdit) return;
    if (issues.length) { setSaveState('invalid'); return; }
    setSaveState('saving');
    try {
      const r = await put<{ revision: number }>(`/admin/landings/${variantId}/draft`, { content, expectedRevision: revision });
      savedJson.current = JSON.stringify(content);
      setRevision(r.revision);
      setSaveState('saved');
      setSavedAt(Date.now());
      setSaveMsg(null);
      sessionStorage.removeItem(storeKey);
      void qc.invalidateQueries({ queryKey: ['landings'] });
    } catch (e) {
      const err = e as ApiClientError;
      if (err.code === 'VERSION_CONFLICT') { setSaveState('conflict'); setSaveMsg(err.message); }
      else if (err.code === 'NETWORK') { setSaveState('offline'); setSaveMsg(err.message); }
      else if (err.status === 401) { setSaveState('error'); setSaveMsg('Your session ended. Your changes are kept in this tab; sign in again in another tab, then press Save.'); }
      else { setSaveState('error'); setSaveMsg(`${err.message}${err.requestId ? ` (Ref ${err.requestId})` : ''}`); }
    }
  }, [content, canEdit, issues.length, variantId, revision, qc, storeKey]);

  // Autosave (debounced) while valid and not in a blocking state.
  useEffect(() => {
    if (!dirty) { if (saveState === 'dirty' || saveState === 'invalid') setSaveState('saved'); return; }
    if (issues.length) { setSaveState('invalid'); return; }
    if (['conflict', 'saving'].includes(saveState)) return;
    if (saveState !== 'offline' && saveState !== 'error') setSaveState('dirty');
    const t = setTimeout(() => void save(), saveState === 'offline' || saveState === 'error' ? 5000 : 1200);
    return () => clearTimeout(t);
  }, [content, dirty, issues.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retry after a failed/offline save, and immediately when the browser reports connectivity again.
  useEffect(() => {
    if (saveState !== 'offline' && saveState !== 'error') return;
    const t = setTimeout(() => void save(), 5000);
    const online = () => void save();
    window.addEventListener('online', online);
    return () => { clearTimeout(t); window.removeEventListener('online', online); };
  }, [saveState, save]);

  const mutate = (fn: (c: LandingContent) => LandingContent) => setContent((c) => (c && canEdit ? fn(c) : c));
  const replaceBlock = (id: string, next: Block) => mutate((c) => ({ ...c, blocks: c.blocks.map((b) => (b.id === id ? next : b)) }));
  const move = (i: number, d: -1 | 1) => mutate((c) => { const b = [...c.blocks]; const j = i + d; if (j < 0 || j >= b.length) return c; [b[i], b[j]] = [b[j]!, b[i]!]; return { ...c, blocks: b }; });
  const add = (t: BlockType) => { const b = newBlock(t); mutate((c) => ({ ...c, blocks: [...c.blocks, b] })); setSelected(b.id); };
  const remove = (id: string) => {
    if (!content) return;
    setUndo(content);
    mutate((c) => ({ ...c, blocks: c.blocks.filter((b) => b.id !== id) }));
    setSelected('seo');
    requestAnimationFrame(() => navRef.current?.querySelector<HTMLElement>('button')?.focus());
    toast({ tone: 'info', message: 'Section removed. Use Undo in the header to restore it before saving.' });
  };

  const reload = async () => {
    sessionStorage.removeItem(storeKey);
    setSaveState('saved'); setSaveMsg(null); setRecovered(false);
    await qc.invalidateQueries({ queryKey: ['draft', variantId] });
    await draftQ.refetch();
  };

  async function openPreview() {
    try {
      const t = await post<{ token: string }>(`/admin/landings/${variantId}/preview-token`);
      const d0 = draftQ.data!;
      // The public URL prefix of this language: everything in the public path before the slug ('' for the default language).
      const pp = d0.publicPath ?? `/${d0.slug}`;
      const prefix = d0.slug === '' ? (pp === '/' ? '' : pp) : pp.slice(0, pp.length - d0.slug.length - 1);
      window.open(`${__PUBLIC_WEB_URL__}${prefix}/preview/${variantId}?token=${encodeURIComponent(t.token)}`, '_blank', 'noopener');
    } catch (e) { toast({ tone: 'error', message: (e as ApiClientError).message }); }
  }

  async function publish() {
    setPublishing(true); setPublishError(null);
    try {
      const r = await post<PublishResult>(`/admin/landings/${variantId}/publish`, { expectedRevision: revision, ...(summary.trim() ? { changeSummary: summary.trim() } : {}) });
      setSummary('');
      toast({ tone: 'success', message: `Published version ${r.versionNumber} at ${r.publicPath}.` });
      setPublishOpen(false);
      void qc.invalidateQueries({ queryKey: ['landings'] });
      await reload();
    } catch (e) { setPublishError(e as ApiClientError); } finally { setPublishing(false); }
  }

  /** Workflow transitions (review, schedule, unpublish…). Each refreshes the draft so the header reflects the new state. */
  async function workflow(path: string, message: string, opts: { method?: 'post' | 'del'; body?: unknown } = {}) {
    setWfBusy(true);
    try {
      if (opts.method === 'del') await del(`/admin/landings/${variantId}${path}`); else await post(`/admin/landings/${variantId}${path}`, opts.body);
      toast({ tone: 'success', message });
      void qc.invalidateQueries({ queryKey: ['landings'] });
      await qc.invalidateQueries({ queryKey: ['draft', variantId] });
    } catch (e) { toast({ tone: 'error', message: (e as ApiClientError).message }); } finally { setWfBusy(false); }
  }
  const refreshDraft = () => { void qc.invalidateQueries({ queryKey: ['landings'] }); void qc.invalidateQueries({ queryKey: ['draft', variantId] }); };

  if (draftQ.isLoading) return <div role="status" style={{ display: 'grid', gap: 12 }}><span className="ag-visually-hidden">Loading editor…</span><Skeleton width={260} height={24} /><Skeleton height={320} /></div>;
  if (draftQ.isError) return <QueryError error={draftQ.error} onRetry={() => void draftQ.refetch()} />;
  if (!content || !draftQ.data) return null;
  const d = draftQ.data;
  const state = lifecycleOf({ state: (d.state ?? 'DRAFT') as 'DRAFT' | 'REVIEW' | 'SCHEDULED' | 'ARCHIVED', publishedVersionNumber: d.publishedVersionNumber, hasUnpublishedChanges: true });
  const archived = state === 'archived';
  const sel = content.blocks.find((b) => b.id === selected);
  const idx = content.blocks.findIndex((b) => b.id === selected);
  const blockIssues = (i: number) => issues.filter((x) => x.path[0] === 'blocks' && x.path[1] === String(i));
  const seoIssues = issues.filter((x) => x.path[0] === 'seo');
  const publishChecks = [
    { ok: content.blocks.some((b) => b.type === 'hero'), label: 'A hero section is present' },
    { ok: content.blocks.some((b) => b.type === 'form'), label: 'An inquiry form section is present' },
    { ok: content.seo.robots === 'index' || !['SERVICE', 'SOLUTION', 'INDUSTRY', 'SEO'].includes(d.kind ?? ''), label: 'Search engines may index the page (required for this page type)' },
    { ok: issues.length === 0, label: 'All fields are valid' },
    { ok: !hasPlaceholder(content), label: 'No placeholder copy left (“[Edit] …”)' },
    { ok: saveState === 'saved' && !dirty, label: 'All changes are saved' },
  ];
  const stateText: Record<SaveState, ReactNode> = {
    saved: <><Icon name="check" /> Saved</>,
    dirty: <>Unsaved changes…</>,
    saving: <><span className="ag-spinner" aria-hidden="true" /> Saving…</>,
    offline: <><Icon name="alert" /> Offline. Retrying; changes kept</>,
    conflict: <><Icon name="alert" /> Conflict</>,
    invalid: <><Icon name="alert" /> Not saved: {issues.length} field{issues.length === 1 ? '' : 's'} need attention</>,
    error: <><Icon name="alert" /> Save failed</>,
  };

  return (
    <div className="editor-wrap">
      <div className="editor__head">
        <Link to="/landings" aria-label="Back to landings" className="ag-iconbtn" style={{ textDecoration: 'none' }}><Icon name="chevronLeft" size={20} /></Link>
        <div>
          <h1 className="editor__title">{d.name}</h1>
          <span className="ag-mono">{d.publicPath ?? `/${d.slug}`}</span> <span className="ag-hint">· {d.locale}</span>
        </div>
        <Badge tone={d.publishedVersionNumber === null ? 'neutral' : 'info'}>{d.publishedVersionNumber === null ? 'Never published' : `Live: v${d.publishedVersionNumber}`}</Badge>
        {state !== 'draft' && state !== 'published' && state !== 'published-changes' ? <Badge tone={state === 'archived' ? 'neutral' : 'info'}>{{ review: 'In review', scheduled: d.scheduledFor ? `Scheduled ${fmtDate(d.scheduledFor)}` : 'Scheduled', archived: 'Archived' }[state]}</Badge> : null}
        <span className="ag-mono" title={`Draft updated ${fmtDate(d.updatedAt)}`}>Draft rev {revision}</span>
        <span className="savebar">{stateText[saveState]}</span>
        <span className="ag-visually-hidden" role="status">{announce}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {undo ? <Button size="sm" variant="ghost" onClick={() => { setContent(undo); setUndo(null); }}>Undo remove</Button> : null}
          <Segmented<Device> label="Preview width" value={device} onChange={setDevice} options={[{ id: 'desktop', label: 'Desktop' }, { id: 'tablet', label: 'Tablet' }, { id: 'mobile', label: 'Mobile' }]} />
          <Button size="sm" icon="eye" onClick={() => void openPreview()}>Preview</Button>
          {(saveState === 'invalid' || saveState === 'error' || saveState === 'offline') ? <Button size="sm" onClick={() => void save()} disabled={issues.length > 0}>Save now</Button> : null}
          <Button size="sm" onClick={() => setChecksOpen(true)}>Checks</Button>
          <Button size="sm" icon="clock" onClick={() => setVersionsOpen(true)} >History</Button>
          {canEdit && state === 'draft' && !archived ? <Button size="sm" loading={wfBusy} disabled={dirty || saveState !== 'saved'} onClick={() => void workflow('/request-review', 'Sent for review.', { body: { expectedRevision: revision } })}>Request review</Button> : null}
          {canPublish && state === 'review' ? <Button size="sm" loading={wfBusy} onClick={() => void workflow('/request-changes', 'Sent back to draft.')}>Request changes</Button> : null}
          {canPublish && state === 'scheduled' ? <Button size="sm" loading={wfBusy} onClick={() => void workflow('/schedule', 'Schedule cancelled.', { method: 'del' })}>Cancel schedule</Button> : null}
          {canPublish && !archived && state !== 'scheduled' ? <Button size="sm" icon="clock" disabled={dirty || saveState !== 'saved'} onClick={() => setScheduleOpen(true)}>Schedule…</Button> : null}
          {canPublish && d.publishedVersionNumber !== null && !archived ? <Button size="sm" variant="danger" loading={wfBusy} onClick={() => setUnpublishOpen(true)}>Unpublish</Button> : null}
          {canPublish && !archived && state !== 'scheduled' ? <Button size="sm" variant="primary" icon="send" onClick={() => { setPublishError(null); setPublishOpen(true); }}>Publish…</Button> : null}
        </div>
      </div>
      {!canEdit ? <div style={{ margin: '12px 0' }}><Notice tone="info" title="Read-only">Your role can view this landing but not edit it.</Notice></div> : null}
      {recovered ? <div style={{ margin: '12px 0' }}><Notice tone="info" title="Unsaved changes recovered">We restored edits from this browser that were not saved. They will save automatically.</Notice></div> : null}
      {saveState === 'conflict' ? <div style={{ margin: '12px 0' }}><ConflictNotice live={false} message={saveMsg ?? 'This draft was changed elsewhere.'} onReload={() => void reload()} /></div> : null}
      {(saveState === 'offline' || saveState === 'error') && saveMsg ? <div style={{ margin: '12px 0' }}><Notice live={false} tone="warning" title="Could not save">{saveMsg}</Notice></div> : null}

      <div className="editor">
        <nav className="editor__nav" aria-label="Sections" ref={navRef}>
          <ul className="sec-list">
            {content.blocks.map((b, i) => (
              <li key={b.id} style={{ display: 'flex', gap: 4 }}>
                <button type="button" className="sec-item" aria-current={b.id === selected} onClick={() => setSelected(b.id)}>
                  <span style={{ flex: 1 }}>{BLOCK_LABEL[b.type]}</span>
                  {blockIssues(i).length ? <Badge tone="error">{blockIssues(i).length}<span className="ag-visually-hidden"> issues</span></Badge> : null}
                </button>
              </li>
            ))}
            <li><button type="button" className="sec-item" aria-current={selected === 'seo'} onClick={() => setSelected('seo')}><span style={{ flex: 1 }}>Page and SEO</span>{seoIssues.length ? <Badge tone="error">{seoIssues.length}<span className="ag-visually-hidden"> issues</span></Badge> : null}</button></li>
          </ul>
          {canEdit ? (
            <div style={{ marginTop: 16 }}>
              <SelectField label="Add section" value={addType} onChange={(e) => setAddType(e.target.value as '' | BlockType)}>
                <option value="">Choose a type…</option>
                {GROUP_ORDER.map((g) => (
                  <optgroup key={g} label={g}>
                    {Object.values(blockCatalog).filter((c) => c.group === g).map((c) => <option key={c.type} value={c.type} disabled={c.single && content.blocks.some((b) => b.type === c.type)}>{c.label}{c.single && content.blocks.some((b) => b.type === c.type) ? ' (already added)' : ''}</option>)}
                  </optgroup>
                ))}
              </SelectField>
              <div style={{ marginTop: 8 }}><Button size="sm" icon="plus" disabled={!addType} onClick={() => { if (addType) { add(addType); setAddType(''); } }}>Add section</Button></div>
            </div>
          ) : null}
        </nav>

        <div className="editor__canvas" ref={canvasRef} tabIndex={0} role="region" aria-label="Visual preview (scrollable)">
          {/* Visual preview only: inert so it adds no tab stops, second h1 or form landmark. Use Preview for the real page. */}
          <div className="editor__frame" data-device={device} style={{ zoom }} inert>
            <div className="pb-root" data-theme="public">
              {renderBlocks(content.blocks, { renderForm: () => <CanvasForm />, showDraftClaims: true })}
            </div>
          </div>
        </div>

        <aside className="editor__inspector" aria-label="Properties">
          <div className="inspector" ref={inspectorRef} tabIndex={-1}>
            {selected === 'seo' ? (
              <>
                <h2>Page and SEO</h2>
                <TextField label="Search title" value={content.seo.title} maxLength={140} readOnly={!canEdit} hint={`${content.seo.title.length}/70 characters`} error={seoIssues.find((x) => x.path[1] === 'title')?.message} onChange={(e) => mutate((c) => ({ ...c, seo: { ...c.seo, title: e.target.value } }))} />
                <TextAreaField label="Meta description" value={content.seo.description} rows={3} maxLength={340} readOnly={!canEdit} hint={`${content.seo.description.length}/170 characters`} error={seoIssues.find((x) => x.path[1] === 'description')?.message} onChange={(e) => mutate((c) => ({ ...c, seo: { ...c.seo, description: e.target.value } }))} />
                <SelectField label="Indexing" value={content.seo.robots} disabled={!canEdit} onChange={(e) => mutate((c) => ({ ...c, seo: { ...c.seo, robots: e.target.value as 'index' | 'noindex' } }))}>
                  <option value="index">Allow indexing</option>
                  <option value="noindex">{d.kind === 'CAMPAIGN' ? 'Do not index (campaign default)' : 'Do not index'}</option>
                </SelectField>
                <PageSettings draft={d} canEdit={canEdit && !archived} onChanged={refreshDraft} />
              </>
            ) : sel ? (
              <BlockInspector block={sel} index={idx} count={content.blocks.length} issues={blockIssues(idx)} canEdit={canEdit} onChange={(next) => replaceBlock(sel.id, next)} onMove={(dir) => move(idx, dir)} onRemove={() => remove(sel.id)} />
            ) : (
              <EmptyState title="Select a section" icon="layout">Choose a section on the left to edit its properties.</EmptyState>
            )}
          </div>
        </aside>
      </div>

      <VersionsDrawer open={versionsOpen} onClose={() => setVersionsOpen(false)} variantId={variantId} revision={revision} canEdit={canEdit && !archived} dirty={dirty || saveState !== 'saved'} onRestored={() => void reload()} />
      <Drawer open={unpublishOpen} onClose={() => setUnpublishOpen(false)} title="Take this page offline?"
        footer={<><Button onClick={() => setUnpublishOpen(false)}>Cancel</Button><Button variant="danger" loading={wfBusy} onClick={() => void workflow('/unpublish', 'Page taken offline.').then(() => setUnpublishOpen(false))}>Unpublish</Button></>}>
        <p>Visitors to <span className="ag-mono">{d.publicPath ?? `/${d.slug}`}</span> will get a “not found” page until it is published again. Versions, leads and history are kept. Add a redirect under SEO if the address should send people elsewhere.</p>
      </Drawer>
      <ChecksDrawer open={checksOpen} onClose={() => setChecksOpen(false)} variantId={variantId} savedAt={savedAt} />
      <ScheduleDrawer open={scheduleOpen} onClose={() => setScheduleOpen(false)} variantId={variantId} revision={revision} onDone={refreshDraft} />
      <Drawer open={publishOpen} onClose={() => setPublishOpen(false)} title="Review and publish"
        footer={<><Button onClick={() => setPublishOpen(false)}>Cancel</Button><Button variant="primary" loading={publishing} disabled={!publishChecks.every((c) => c.ok)} onClick={() => void publish()}>Publish version {(d.publishedVersionNumber ?? 0) + 1}</Button></>}>
        <p style={{ marginBottom: 12 }}>Publishing creates version <strong>{(d.publishedVersionNumber ?? 0) + 1}</strong> of <strong>{d.name}</strong> ({d.locale}). It becomes visible at <span className="ag-mono">{d.publicPath ?? `/${d.slug}`}</span> immediately. Earlier versions and the leads that came from them are not changed.</p>
        {publishChecks.every((c) => c.ok) ? (
          <details>
            <summary><Badge tone="success">{publishChecks.length} checks passed</Badge></summary>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>{publishChecks.map((c) => <li key={c.label}>{c.label}</li>)}</ul>
          </details>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }} aria-label="Publishing checks">
            {publishChecks.filter((c) => !c.ok).map((c) => (
              <li key={c.label} style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Badge tone="error">Fix</Badge> {c.label}</li>
            ))}
          </ul>
        )}
        <div style={{ marginTop: 16 }}><TextField label="What changed (optional)" value={summary} maxLength={240} onChange={(e) => setSummary(e.target.value)} hint="Shown in the version history." /></div>
        {publishError ? <div style={{ marginTop: 16 }}><Notice tone="error" title="Publishing failed">{publishError.message}{publishError.fieldErrors.length ? <ul>{publishError.fieldErrors.map((f) => <li key={f.message}>{f.message}</li>)}</ul> : null}{publishError.requestId ? ` (Ref ${publishError.requestId})` : ''}</Notice></div> : null}
        {!publishChecks.every((c) => c.ok) ? <p style={{ marginTop: 16, color: 'var(--c-text-2)' }}>Resolve the items marked “Fix” to enable publishing.</p> : null}
      </Drawer>
    </div>
  );
}
